/**
 * Ícono de tipo de conector del back-office: los mismos trazos de marca que la app para CCS1, CCS2
 * y GB/T (docs/marca/conectores, paquete del dueño del 23-10-2026), como SVG en línea que toma el
 * color del texto (`currentColor`). Para los demás estándares no hay ícono y se muestra el nombre
 * comercial en texto. `ConnectorLabel` pone el ícono junto al nombre y `ConnectorSummary` resume
 * los conectores de un cargador con un ícono por tipo (`2 × CCS2 + CCS1 · 180 kW`).
 */
import { type CSSProperties, Fragment } from 'react';
import { connectorGroups, connectorGroupsText } from '../lib/connector-groups.ts';
import { type ConnectorLike, connectorStandard, type Locale } from '../lib/format.ts';
import { CONNECTOR_VIEW_BOX, type ConnectorVariant, connectorGlyph } from './connector-paths.ts';

export type { ConnectorVariant } from './connector-paths.ts';

/** En línea con el texto, sin encogerse dentro de un `.row` o de una celda. */
const iconStyle: CSSProperties = { display: 'inline-block', verticalAlign: '-0.2em', flex: 'none' };
const labelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
};

export function ConnectorIcon({
  standard,
  size = 20,
  variant = 'line',
  locale = 'es',
  decorative = false,
}: {
  standard: string | null | undefined;
  size?: number | undefined;
  variant?: ConnectorVariant | undefined;
  /** Idioma del texto de reserva para los estándares sin ícono. */
  locale?: Locale | undefined;
  /** Cuando el nombre ya está escrito al lado: el ícono no se anuncia dos veces. */
  decorative?: boolean | undefined;
}) {
  const glyph = connectorGlyph(standard, variant);
  if (!glyph) return <span>{connectorStandard(standard, locale)}</span>;
  return (
    <svg
      viewBox={CONNECTOR_VIEW_BOX}
      width={size}
      height={size}
      fill="currentColor"
      style={iconStyle}
      focusable="false"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : glyph.label}
      aria-hidden={decorative ? true : undefined}
    >
      <path fillRule="evenodd" d={glyph.d} />
    </svg>
  );
}

/** Ícono (si el estándar lo tiene) y nombre comercial: `[CCS2] CCS2`, `Tipo 2`. */
export function ConnectorLabel({
  standard,
  locale,
  size = 20,
  variant = 'line',
}: {
  standard: string | null | undefined;
  locale: Locale;
  size?: number | undefined;
  variant?: ConnectorVariant | undefined;
}) {
  const label = connectorStandard(standard, locale);
  if (!connectorGlyph(standard, variant)) return <>{label}</>;
  return (
    <span style={labelStyle}>
      <ConnectorIcon standard={standard} size={size} variant={variant} decorative />
      {label}
    </span>
  );
}

/** Resumen de los conectores de un cargador con un ícono por tipo, como `connectorSummary`. */
export function ConnectorSummary({
  connectors,
  locale,
  size = 20,
  variant = 'line',
}: {
  connectors: readonly ConnectorLike[] | null | undefined;
  locale: Locale;
  size?: number | undefined;
  variant?: ConnectorVariant | undefined;
}) {
  const summary = connectorGroups(connectors, locale);
  if (summary.groups.length === 0) return <>—</>;
  return (
    <span title={connectorGroupsText(summary)}>
      {summary.groups.map((group, index) => (
        <Fragment key={group.label}>
          {index > 0 ? ' + ' : null}
          {group.count > 1 ? `${group.count} × ` : null}
          <ConnectorLabel standard={group.standard} locale={locale} size={size} variant={variant} />
        </Fragment>
      ))}
      {summary.power ? ` · ${summary.power}` : null}
    </span>
  );
}
